import { createAsyncThunk } from '@reduxjs/toolkit';
import { $apiV1, $apiV2 } from '../../config/api';
import { trackEntitySearch } from '../../tracking';

export const searchPlayers = createAsyncThunk(
  'eventsExplorer/searchPlayers',
  async (query, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get('/platform/player/', {
        params: { search: query, page_size: 15 }
      });
      trackEntitySearch({
        entityType: 'player',
        query,
        resultCount: response.data?.count,
        results: response.data?.results || [],
        sourceSurface: 'events_explorer',
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const searchTeams = createAsyncThunk(
  'eventsExplorer/searchTeams',
  async (query, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get('/platform/team/', {
        params: { search: query, page_size: 15 }
      });
      trackEntitySearch({
        entityType: 'team',
        query,
        resultCount: response.data?.count,
        results: response.data?.results || [],
        sourceSurface: 'events_explorer',
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTeamRoster = createAsyncThunk(
  'eventsExplorer/fetchTeamRoster',
  async ({ teamId, seasonId }, { rejectWithValue }) => {
    try {
      // Fetch both team statistics (for players_list) and players_opt (for stats data)
      const statsUrl = seasonId
        ? `/statistics/team_opt/${teamId}?year_season_id=${seasonId}`
        : `/statistics/team_opt/${teamId}`;

      const [statsResponse, playersOptResponse] = await Promise.all([
        $apiV1.get(statsUrl),
        $apiV2.get(`/platform/team/${teamId}/players_opt`, {
          params: seasonId ? { year_season_id: seasonId } : {}
        })
      ]);

      const playersList = statsResponse.data.players_list || [];
      const playersOpt = playersOptResponse.data?.results || playersOptResponse.data || [];

      // If players_list has data, use it (has display_name, position, photo)
      if (playersList.length > 0) {
        return playersList;
      }

      // Otherwise use players_opt and build display names from name + surname
      return playersOpt.map(player => ({
        ...player,
        display_name: player.display_name || `${player.name || ''} ${player.surname || ''}`.trim() || 'Unknown'
      }));
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchPlayerSeasons = createAsyncThunk(
  'eventsExplorer/fetchPlayerSeasons',
  async (playerId, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get(`/statistics/player_opt/${playerId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchPlayerParameters = createAsyncThunk(
  'eventsExplorer/fetchPlayerParameters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get('/stats/parameters/player/');
      return response.data?.results || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAggregatedEvents = createAsyncThunk(
  'eventsExplorer/fetchAggregatedEvents',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { eventsExplorer } = getState();
      const { selectedPlayers, globalParameters } = eventsExplorer;

      // Only players with configured matches can be fetched — the stats API
      // requires `matches=`, and one unconfigured player would otherwise fail
      // its request and poison the whole Promise.all (all events silently lost).
      const activePlayers = selectedPlayers.filter(
        p => p.isActive && (p.config?.matchIds?.length || 0) > 0
      );
      if (activePlayers.length === 0) return [];

      // Fetch markers for each player in parallel
      const markerPromises = activePlayers.map(async (player) => {
        const paramIds = player.config.useGlobalParams
          ? globalParameters.checkedIds
          : player.config.parameterIds;

        if (paramIds.length === 0) return { playerId: player.id, markers: [], markerParams: {}, color: player.color, team: player.team };

        const params = {
          params: paramIds.join(','),
          matches: player.config.matchIds.join(',')
        };

        let response;
        try {
          response = await $apiV1.get(`/stats/player/${player.id}`, { params });
        } catch (err) {
          // One player's failed stats request must not poison every player's
          // events — degrade to no markers for this player only.
          console.warn(`[eventsExplorer] stats fetch failed for player ${player.id}:`, err.message);
          return { playerId: player.id, markers: [], markerParams: {}, color: player.color, team: player.team };
        }

        // Record which parameter(s) each marker was fetched under. Metric
        // computation relies on this: event action names (e.g. "Blocked shot")
        // do NOT match parameter titles (e.g. "Shots"), so parameter
        // membership can only be known at fetch time.
        const markerParams = {};
        const markers = response.data.stats?.flatMap((stat) =>
          stat.parameters?.flatMap((param) => {
            (param.markers || []).forEach((markerId) => {
              if (!markerParams[markerId]) markerParams[markerId] = [];
              markerParams[markerId].push({
                id: param.parameter_id,
                name: param.parameter_name
              });
            });
            return param.markers;
          }) || []
        ) || [];

        return { playerId: player.id, markers, markerParams, color: player.color, team: player.team };
      });

      const markerResults = await Promise.all(markerPromises);

      // Build a map of marker ID to player info (color, team, etc.)
      const markerToPlayer = {};
      const markerToParams = {};
      markerResults.forEach(result => {
        result.markers.forEach(markerId => {
          markerToPlayer[markerId] = {
            color: result.color,
            playerId: result.playerId,
            team: result.team
          };
        });
        Object.entries(result.markerParams).forEach(([markerId, params]) => {
          markerToParams[markerId] = params;
        });
      });

      // Flatten all markers
      const allMarkers = markerResults.flatMap(r => r.markers);

      if (allMarkers.length === 0) return [];

      // Fetch events for all markers
      const eventsResponse = await $apiV1.post('/stats/events_search/', {
        markers: allMarkers
      });

      // Tag events with player color/team and source parameters based on the
      // marker they came from (event.id IS the marker id).
      const events = (eventsResponse.data.events || []).map(event => {
        const playerInfo = markerToPlayer[event.id];
        const params = markerToParams[event.id] || [];
        return {
          ...event,
          playerColor: playerInfo?.color || '#040802',
          playerId: playerInfo?.playerId || event.creator?.id,
          playerTeam: playerInfo?.team || null,
          paramIds: params.map(p => p.id),
          paramNames: [...new Set(params.map(p => p.name).filter(Boolean))]
        };
      });

      return events.sort((a, b) => a.second - b.second);

    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
